import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

type State = {
  [k in SkuTypes.TypeInfo]: SkuTypes.PropTypes & {
    mapInfo: InventoryTypes.SkuInfoMap;
  };
};

type Action = {
  resetSku: () => void;
  setNoneSku: (payload: {
    type: SkuTypes.TypeInfo;
    inventoryMap: InventoryTypes.SkuInfoMap;
  }) => void;
  setSkuSelect: (type: SkuTypes.TypeInfo, item?: SkuTypes.Value) => void;
  setSkuImageReview: (type: SkuTypes.TypeInfo, item: SkuTypes.Value, showType?: string) => void;
  setIsSkuImage: (type: SkuTypes.TypeInfo, item: SkuTypes.Value) => void;
  setSkuImageNone: (type: SkuTypes.TypeInfo) => void;
  setSkuNumber: (type: SkuTypes.TypeInfo, key: string, number: number) => void;
  setTotalPrice: (payload: {
    type: SkuTypes.TypeInfo;
    isMoqPrice?: boolean;
    rule: PriceTypes.Root;
    moq: number;
  }) => void;
  setSkuInfo: (
    type: SkuTypes.TypeInfo,
    skuAttrs: SkuTypes.SkuAttr[],
    inventorySkuMap: InventoryTypes.SkuInfoMap,
    skuInfoMap: InventoryTypes.SkuInfoMap,
    dispatchId?: string,
  ) => SkuTypes.SkuAttr[];
  setNoneQuantity: (quantity: number) => void;
};

const defaultObj = {
  mapInfo: {},
  // 待替换
  map: {},
  attr: [],
  dispatchId: '',
  selectedKey: '',
  leadTime: 0,
  quantity: 0,
  isSoldOut: false,
  moqPrice: 0,
  totalPrice: 0,
};

const SkuStore = create<State & Action>()(
  immer((set) => ({
    product: defaultObj,
    sample: defaultObj,
    inquiry: defaultObj,
    // 解决quick view多次调用
    resetSku: () => {
      set((state) => {
        state.product = defaultObj;
      });
    },
    /**
     * 重新整理sku并合并库存信息
     * 1、图片长度 > 图片 > 文字
     * 2、合并库存信息
     * 3、更新映射map支持图片排序
     * 4、默认选择更新
     */
    setSkuInfo: (type, skuAttrs, inventorySkuMap, skuInfoMap, dispatchId = '') => {
      const result: SkuTypes.SkuAttr[] = [];
      const productsWithImage: SkuTypes.SkuAttr[] = [];
      const productsWithColor: SkuTypes.SkuAttr[] = [];
      const _addAttrId = (product: SkuTypes.SkuAttr) => {
        return {
          ...product,
          values: product.values.map((i) => ({
            ...i,
            attrId: product.id,
          })),
        };
      };
      // 图片顺序前置
      skuAttrs.forEach((product) => {
        if (product.values.some((value) => value.type === 'IMAGE')) {
          productsWithImage.push(_addAttrId(product));
        } else if (product.values.some((value) => value.type === 'COLOR')) {
          productsWithColor.push(_addAttrId(product));
        } else {
          result.push(_addAttrId(product));
        }
      });
      const attr = productsWithImage
        .sort((a, b) => b.values.length - a.values.length)
        .concat(productsWithColor)
        .concat(result);

      // 无sku不处理
      if (attr.length === 0) return [];

      // 更新map顺序
      const attrIndex = attr.map((i) => `${i.id}`);

      // Map映射根据顺序重置
      const resetKey = (key: string): string => {
        const arr = key
          .split(';')
          .map((item) => item.split(':'))
          .sort((a, b) => attrIndex.indexOf(a[0]) - attrIndex.indexOf(b[0]))
          .map((item) => item.join(':'))
          .filter((i) => i);
        return arr.join(';');
      };

      // 赋值库存信息
      const tempInfoMap: InventoryTypes.SkuInfoMap = {};
      if (inventorySkuMap) {
        Object.keys(skuInfoMap).forEach((i) => {
          const _mapKey = resetKey(i);
          const _skuId = skuInfoMap[i].skuId || skuInfoMap[i].id || '';
          const skuItem = inventorySkuMap[_skuId];

          if (skuItem) {
            skuItem.inventoryCount =
              skuItem.inventoryCount === -1 ? 999999999 : skuItem.inventoryCount;
            tempInfoMap[_mapKey] = {
              skuId: _skuId,
              ...skuInfoMap[i],
              ...inventorySkuMap[_skuId],
            };
          }
        });
      }
      // 更新数据
      set((state) => {
        if (state[type].attr.length === 0 || dispatchId !== state[type].dispatchId) {
          state[type].attr = attr;
          state[type].dispatchId = dispatchId;
          state[type].mapInfo = tempInfoMap;
          state[type].dispatchId = dispatchId;
          state[type].isSoldOut =
            Object.values(tempInfoMap).reduce(
              (a: number, { inventoryCount }) => a + inventoryCount,
              0,
            ) === 0;
        }
      });
      return attr;
    },
    setSkuSelect: (type, item) => {
      set((state) => {
        const { attr = [] } = state[type];
        let attrMap = attr;
        if (item) {
          attrMap = attr.map((i) =>
            i.id === item.attrId
              ? {
                  ...i,
                  values: i.values.map((v) =>
                    v.id === item.id
                      ? {
                          ...v,
                          selected: true,
                        }
                      : {
                          ...v,
                          selected: false,
                        },
                  ),
                }
              : i,
          );
          state[type].attr = attrMap;
        }
        const selectedArr = attrMap
          .flatMap((obj) => obj.values.filter((val) => val.selected))
          .map((i: SkuTypes.Value) => `${i.attrId}:${i.id}`);
        state[type].selectedKey = selectedArr.join(';');
      });
    },
    setTotalPrice: (payload) => {
      const { rule, type, moq, isMoqPrice = false } = payload;
      set((state) => {
        // 样品价格
        const { quantity, mapInfo } = state[type];
        // 检测价格
        let moqPrice = 0;
        moqPrice = Object.values(mapInfo)
          .filter((i) => i.quantity > 0)
          .reduce(
            (acc, { quantity = 0, price = 0, promotionPrice = 0 }) =>
              acc + quantity * (promotionPrice || price),
            0,
          );
        if (moqPrice > 0) {
          state[type].totalPrice = moqPrice;
        } else {
          const moqQuantity = Math.max(moq, quantity);
          const { productLadderPrices, fixedPrice, promotionFixedPrice, productRangePrices } = rule;

          // 阶梯价
          if (productLadderPrices) {
            const ladderPrice = productLadderPrices.find(
              (item) => item.min <= moqQuantity && (item.max === -1 || item.max >= moqQuantity),
            );
            moqPrice = ladderPrice?.promotionPrice || ladderPrice?.price || 0;
            if (isMoqPrice) {
              state[type].moqPrice = moqPrice;
            } else {
              // 更新sku价格
              state[type].price = ladderPrice?.price;
              state[type].promotionPrice = ladderPrice?.promotionPrice;
              state[type].totalPrice = moqPrice * quantity;
            }
          }
          // 区间价
          else if (productRangePrices) {
            const { priceRangePromotionLow, priceRangeLow } = productRangePrices;
            state[type].totalPrice = (priceRangePromotionLow || priceRangeLow) * quantity;
          } else if (fixedPrice) {
            state[type].price = fixedPrice;
            state[type].promotionPrice = promotionFixedPrice;
            state[type].totalPrice = (promotionFixedPrice || fixedPrice) * quantity;
          } else {
            moqPrice = Object.values(mapInfo)
              .filter((i) => i.quantity > 0)
              .reduce(
                (acc, { quantity = 0, price = 0, promotionPrice = 0 }) =>
                  acc + quantity * (promotionPrice || price),
                0,
              );
            if (isMoqPrice) {
              state[type].moqPrice = moqPrice;
            } else {
              state[type].totalPrice = moqPrice;
            }
          }
        }
      });
    },
    setSkuImageReview: (type, item, showType = '') => {
      set((state) => {
        state[type].skuActiveId = item.id;
        state[type].skuShowType = showType;
        if (item.largeImage) {
          state[type].skuImageReview = item;
        }
      });
    },
    // 用来标识是否选中了skuImage
    setIsSkuImage: (type, item) => {
      set((state) => {
        if (item.largeImage) {
          state[type].isSkuImage = true;
        } else state[type].isSkuImage = false;
      });
    },
    // 用来给主图组件display none掉sku图片
    setSkuImageNone: (type) => {
      set((state) => {
        state[type].skuShowType = '';
      });
    },
    setSkuNumber: (type, key, number) => {
      set((state) => {
        const { mapInfo } = state[type];
        state[type].mapInfo[key] = {
          ...mapInfo[key],
          quantity: number,
        };

        state[type].quantity = Object.values(state[type].mapInfo).reduce(
          (acc, { quantity = 0 }) => acc + quantity,
          0,
        );
      });
    },
    setNoneSku: (payload) => {
      const { type, inventoryMap } = payload;
      const { skuMap } = inventoryMap;
      set((state) => {
        state[type] = {
          ...state[type],
          ...skuMap,
        };
      });
    },
    setNoneQuantity: (quantity) => {
      set((state) => {
        state.product.mapInfo = {
          [-1]: {
            quantity,
          },
        };
        state.product.quantity = quantity;
      });
    },
  })),
);

export default SkuStore;
